import { createStore, useStore } from "zustand";
import { useRef, useMemo, useCallback } from "react";
import { showToast } from "@/components/ui/sonner";

const PER_PAGE = 20;

export interface ResolvedItem {
  inputValue: string;
  matched: boolean;
  name?: string;
  domain?: string;
  candidateId?: string;
}

export type CsvResolver = (values: string[]) => Promise<ResolvedItem[]>;

export interface ImportedEntity {
  id: string;
  name: string;
  domain?: string;
  matched: boolean;
  selected: boolean;
  candidateId?: string;
  inputValue: string;
}

export interface CsvImportResult {
  name: string;
  domain?: string;
  candidateId?: string;
  inputValue: string;
}

interface CsvImportState {
  entities: ImportedEntity[];
  totalParsed: number;
  processedCount: number;
  isProcessing: boolean;
  search: string;
  page: number;
  duplicatesRemoved: number;

  processFile: (file: File, resolver: CsvResolver) => Promise<void>;
  toggleEntity: (id: string) => void;
  reset: () => void;
  setSearch: (s: string) => void;
  setPage: (p: number) => void;
}

export type CsvImportStore = ReturnType<typeof createCsvImportStore>;

export function createCsvImportStore() {
  return createStore<CsvImportState>()((set, get) => ({
    entities: [],
    totalParsed: 0,
    processedCount: 0,
    isProcessing: false,
    search: "",
    page: 1,
    duplicatesRemoved: 0,

    processFile: async (file, resolver) => {
      if (file.size > 5 * 1024 * 1024) {
        showToast.error("File too large", { description: "Max 5MB" });
        return;
      }
      if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
        showToast.error("Invalid file type", {
          description: "Only CSV files are accepted",
        });
        return;
      }

      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      const firstLine = lines[0]?.toLowerCase() ?? "";
      const hasHeader =
        firstLine.includes("name") ||
        firstLine.includes("company") ||
        firstLine.includes("linkedin") ||
        firstLine.includes("url");
      const dataLines = hasHeader ? lines.slice(1) : lines;

      const values = dataLines
        .map((line) => {
          const parts = line.split(",");
          return parts[0]?.replace(/^["']|["']$/g, "").trim();
        })
        .filter((n): n is string => !!n && n.length > 0)
        .slice(0, 500);

      if (values.length === 0) {
        showToast.error("No valid entries found in CSV");
        return;
      }

      set((s) => ({
        totalParsed: s.totalParsed + values.length,
        isProcessing: true,
        page: 1,
      }));

      const BATCH = 20;

      for (let i = 0; i < values.length; i += BATCH) {
        const batch = values.slice(i, i + BATCH);
        try {
          const results = await resolver(batch);
          const items: ImportedEntity[] = results.map((r, idx) => ({
            id: `import-${Date.now()}-${i + idx}-${r.inputValue}`,
            name: r.matched ? (r.name ?? r.inputValue) : r.inputValue,
            domain: r.domain,
            matched: r.matched,
            selected: r.matched,
            candidateId: r.candidateId,
            inputValue: r.inputValue,
          }));

          set((s) => {
            const existingKeys = new Set(
              s.entities.map((e) => (e.candidateId ?? e.name).toLowerCase()),
            );
            const unique = items.filter(
              (item) =>
                !existingKeys.has(
                  (item.candidateId ?? item.name).toLowerCase(),
                ),
            );
            const dupeCount = items.length - unique.length;
            return {
              entities: [...s.entities, ...unique],
              duplicatesRemoved: s.duplicatesRemoved + dupeCount,
              processedCount: s.processedCount + batch.length,
            };
          });
        } catch {
          const items: ImportedEntity[] = batch.map((n, idx) => ({
            id: `import-err-${Date.now()}-${i + idx}`,
            name: n,
            matched: false,
            selected: false,
            inputValue: n,
          }));

          set((s) => {
            const existingKeys = new Set(
              s.entities.map((e) => (e.candidateId ?? e.name).toLowerCase()),
            );
            const unique = items.filter(
              (item) =>
                !existingKeys.has(
                  (item.candidateId ?? item.name).toLowerCase(),
                ),
            );
            const dupeCount = items.length - unique.length;
            return {
              entities: [...s.entities, ...unique],
              duplicatesRemoved: s.duplicatesRemoved + dupeCount,
              processedCount: s.processedCount + batch.length,
            };
          });
        }
      }

      set({ isProcessing: false });
    },

    toggleEntity: (id) =>
      set((s) => ({
        entities: s.entities.map((e) =>
          e.id === id ? { ...e, selected: !e.selected } : e,
        ),
      })),

    reset: () =>
      set({
        entities: [],
        totalParsed: 0,
        processedCount: 0,
        isProcessing: false,
        duplicatesRemoved: 0,
        search: "",
        page: 1,
      }),

    setSearch: (search) => set({ search, page: 1 }),
    setPage: (page) => set({ page }),
  }));
}

/**
 * Creates a per-component Zustand store instance for CSV import state.
 * Returns a flat object with raw state, actions, and derived values.
 */
export function useCsvImport() {
  const storeRef = useRef<CsvImportStore>(undefined);
  if (!storeRef.current) storeRef.current = createCsvImportStore();

  const state = useStore(storeRef.current);

  const selectedCount = useMemo(
    () => state.entities.filter((e) => e.selected).length,
    [state.entities],
  );
  const matchedCount = useMemo(
    () => state.entities.filter((e) => e.matched).length,
    [state.entities],
  );
  const unmatchedCount = state.entities.length - matchedCount;

  const matchedPct =
    state.totalParsed > 0 ? (matchedCount / state.totalParsed) * 100 : 0;
  const unmatchedPct =
    state.totalParsed > 0 ? (unmatchedCount / state.totalParsed) * 100 : 0;
  const duplicatePct =
    state.totalParsed > 0
      ? (state.duplicatesRemoved / state.totalParsed) * 100
      : 0;

  const filteredEntities = useMemo(() => {
    if (!state.search) return state.entities;
    const q = state.search.toLowerCase();
    return state.entities.filter((e) => e.name.toLowerCase().includes(q));
  }, [state.entities, state.search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEntities.length / PER_PAGE),
  );
  const pagedEntities = filteredEntities.slice(
    (state.page - 1) * PER_PAGE,
    state.page * PER_PAGE,
  );

  const getSelected = useCallback(
    (): CsvImportResult[] =>
      state.entities
        .filter((e) => e.selected)
        .map((e) => ({
          name: e.name,
          domain: e.domain,
          candidateId: e.candidateId,
          inputValue: e.inputValue,
        })),
    [state.entities],
  );

  return {
    entities: state.entities,
    totalParsed: state.totalParsed,
    processedCount: state.processedCount,
    isProcessing: state.isProcessing,
    search: state.search,
    setSearch: state.setSearch,
    page: state.page,
    setPage: state.setPage,
    duplicatesRemoved: state.duplicatesRemoved,
    processFile: state.processFile,
    toggleEntity: state.toggleEntity,
    reset: state.reset,
    selectedCount,
    filteredEntities,
    totalPages,
    pagedEntities,
    matchedCount,
    unmatchedCount,
    matchedPct,
    unmatchedPct,
    duplicatePct,
    getSelected,
  };
}
